/**
 * Live updates over Server-Sent Events (GET /api/events).
 *
 * Domains register how an event maps to cached queries:
 *
 *   registerLive('recipe.', (evt, qc) => {
 *     qc.invalidateQueries({ queryKey: ['recipes'] });
 *   });
 *
 * On reconnect (or a server 'resync') every query is refetched: there is no
 * replay, so a full refetch is the only safe catch-up.
 */
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

export type LiveEvent = {
  type: string;
  at: string;
  seq?: number;
  by: { id: number | null; name: string | null };
  id?: number;
  version?: number;
  name?: string;
  [k: string]: unknown;
};

type Handler = (evt: LiveEvent, qc: QueryClient) => void;

const handlers: Array<{ prefix: string; fn: Handler }> = [];
const listeners = new Set<(evt: LiveEvent) => void>();

/** Register a handler for every event whose type starts with `prefix`. */
export function registerLive(prefix: string, fn: Handler): void {
  if (!handlers.some((h) => h.prefix === prefix && h.fn === fn)) handlers.push({ prefix, fn });
}

/** Subscribe a component to raw events (e.g. an editor warning that the item
 *  it is editing just changed). Returns an unsubscribe function. */
export function onLiveEvent(fn: (evt: LiveEvent) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function dispatch(evt: LiveEvent, qc: QueryClient) {
  for (const h of handlers) {
    if (evt.type.startsWith(h.prefix)) {
      try {
        h.fn(evt, qc);
      } catch (e) {
        console.error('live handler failed', e);
      }
    }
  }
  // Price/unit changes ripple through every costed view.
  if (evt.type === 'costs.changed') {
    void qc.invalidateQueries({
      predicate: (q) => ['recipes', 'menus', 'dashboard', 'ingredients', 'prep-lists'].includes(String(q.queryKey[0])),
    });
  }
  listeners.forEach((l) => l(evt));
}

export type LiveStatus = 'connecting' | 'live' | 'reconnecting';

/** How long to wait before opening a new stream after the browser has given
 *  up on one, attempt by attempt; the last value repeats. */
export const LIVE_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;
/** A drop has to last this long before the shell says so. A phone unlock or
 *  a server restart is usually back inside it, and the banner would only
 *  push the page down under the chef's thumb and back up again. */
export const LIVE_BANNER_GRACE_MS = 5_000;

/** Mount once inside the signed-in shell. Returns the connection state so
 *  the shell can say when other people's changes are not arriving (UX audit
 *  2026-09-26, F7).
 *
 *  EventSource retries a dropped stream on its own, but gives up for good
 *  when a reconnect answers anything but a 200 event stream: a 502 or 530
 *  from the proxy or tunnel while the server restarts, or a 401 once the
 *  session has ended. So when the stream is CLOSED this opens a new one,
 *  backing off (LIVE_BACKOFF_MS), and straight away when the browser comes
 *  back online or the page becomes visible again. Each give-up also
 *  re-checks the session, so a 401 sends the chef to Log in instead of
 *  retrying for ever. 'reconnecting' is reported only after
 *  LIVE_BANNER_GRACE_MS without a stream, and never before the first one. */
export function useLiveUpdates(enabled: boolean, myUserId: number | null | undefined): LiveStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const me = useRef(myUserId);
  me.current = myUserId;

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let opened = false; // a stream has said hello at least once
    let live = false; // the current stream said hello and has not dropped since
    let stopped = false; // signed out, or unmounted: never reconnect
    let attempt = 0;
    let retryTimer: number | undefined;
    let bannerTimer: number | undefined;

    const clearRetry = () => {
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      retryTimer = undefined;
    };
    const clearBanner = () => {
      if (bannerTimer !== undefined) window.clearTimeout(bannerTimer);
      bannerTimer = undefined;
    };

    const connect = () => {
      if (stopped) return;
      clearRetry();
      es?.close();
      const src = new EventSource('/api/events');
      es = src;

      src.addEventListener('hello', () => {
        clearBanner();
        attempt = 0;
        live = true;
        if (opened) void qc.invalidateQueries(); // reconnect: refetch everything
        opened = true;
        setStatus('live');
      });
      src.onerror = () => {
        if (src !== es || stopped) return;
        live = false;
        if (opened && bannerTimer === undefined) {
          bannerTimer = window.setTimeout(() => {
            bannerTimer = undefined;
            if (!live && !stopped) setStatus('reconnecting');
          }, LIVE_BANNER_GRACE_MS);
        }
        // Still CONNECTING: the browser is retrying by itself. CLOSED: it
        // has given up, and only a new EventSource will try again.
        if (src.readyState !== EventSource.CLOSED) return;
        src.close();
        void qc.invalidateQueries({ queryKey: ['session'] });
        const delay = LIVE_BACKOFF_MS[Math.min(attempt, LIVE_BACKOFF_MS.length - 1)];
        attempt += 1;
        clearRetry();
        retryTimer = window.setTimeout(connect, delay);
      };
      src.addEventListener('resync', () => void qc.invalidateQueries());
      src.addEventListener('signed-out', () => {
        stopped = true;
        clearRetry();
        clearBanner();
        src.close();
        void qc.invalidateQueries({ queryKey: ['session'] });
      });
      src.addEventListener('change', (msg) => {
        try {
          const evt = JSON.parse((msg as MessageEvent).data) as LiveEvent;
          (evt as LiveEvent & { mine?: boolean }).mine = evt.by?.id != null && evt.by.id === me.current;
          dispatch(evt, qc);
        } catch (e) {
          console.error('bad live event', e);
        }
      });
    };

    // Back online, or back on screen (a phone unlocked, a tab brought
    // forward): a stream the browser gave up on is reopened now rather than
    // at the end of its backoff. One that is open or retrying is left alone.
    const wake = () => {
      if (stopped || (es && es.readyState !== EventSource.CLOSED)) return;
      connect();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') wake();
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', onVisible);
    connect();

    return () => {
      stopped = true;
      clearRetry();
      clearBanner();
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', onVisible);
      es?.close();
      es = null;
    };
  }, [enabled, qc]);
  return status;
}

export function isMine(evt: LiveEvent): boolean {
  return Boolean((evt as LiveEvent & { mine?: boolean }).mine);
}
